import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { RecurlyProvider, Elements } from '@recurly/react-recurly';
import { useMutation } from '@tanstack/react-query';
import { useHistory } from 'react-router-dom';
import { getCameraActions, PageLayout } from 'components/PageLayout';
import { getDeviceInfo } from 'state/localStorageTyped';
import {
  useMembershipState,
  MEMBERSHIP_CODES,
  MEMBERSHIP_DESCRIPTION_TRANS_KEYS,
  MEMBERSHIP_TITLE_TRANS_KEYS,
} from 'state/useMembershipState';
import {
  ProcessMembershipPaymentTokenData,
  SavePaymentMethodData,
} from 'shared/types';
import { API } from 'api';
import { RecurlyBillingInformation } from 'features/payments/components/RecurlyBillingInformation';
import { usePaymentApi } from 'hooks/usePaymentApi';
import { usePurchaseTracking } from 'hooks/usePurchaseTracking';
import { SimplePageLayout } from 'components/layouts/SimplePageLayout';
import { ENV } from 'shared/ENV';
import { MEMBERSHIP_PLANS_AMOUNTS } from 'features/membership/membership.constants';
import { formatToUSDollars } from 'shared/utils';

const Content = (): JSX.Element => {
  const { t } = useTranslation();
  const {
    email,
    firstName,
    lastName,
    clientToken,
    optInToUpdates,
    verificationToken,
    userId,
    updatePaymentMethod,
    plan,
  } = useMembershipState();
  const history = useHistory();
  const { logPurchaseEvent } = usePurchaseTracking();

  const { processMembershipPaymentMutation } =
    usePaymentApi<ProcessMembershipPaymentTokenData>({
      onSuccess: () => {
        // Log purchase event for credit card payment
        logPurchaseEvent({
          productId: MEMBERSHIP_CODES[plan],
          price: MEMBERSHIP_PLANS_AMOUNTS[plan],
          transactionType: 'membership',
          paymentMethod: 'credit_card',
          isUpgrade: false,
        });
        history.push('/membership/success');
      },
    });

  const savePaymentMethodMutation = useMutation<
    void,
    Error,
    SavePaymentMethodData,
    unknown
  >({
    mutationFn: async (data) => {
      await API.post('recurly_integration/update_billing_info/', data);
    },
    onSuccess: () => {
      history.push('/membership/account_info');
    },
  });

  return (
    <>
      <Helmet>
        <title>{t('membershipBillingInformation.title')}</title>
      </Helmet>
      <SimplePageLayout>
        <RecurlyProvider publicKey={ENV.VITE_RECURLY_PUBLIC_TOKEN}>
          <Elements>
            <RecurlyBillingInformation<ProcessMembershipPaymentTokenData>
              transactionType="membership"
              initialState={{
                email,
                firstName,
                lastName,
                clientToken,
                optInToUpdates,
                transactionAmount: MEMBERSHIP_PLANS_AMOUNTS[plan],
                verificationToken,
                userId,
                updatePaymentMethod,
              }}
              processPaymentMutation={processMembershipPaymentMutation}
              savePaymentMethodMutation={savePaymentMethodMutation}
              checkoutConfig={{
                code: MEMBERSHIP_CODES[plan],
                title: t(MEMBERSHIP_TITLE_TRANS_KEYS[plan]),
                description: t(MEMBERSHIP_DESCRIPTION_TRANS_KEYS[plan], {
                  price: formatToUSDollars(MEMBERSHIP_PLANS_AMOUNTS[plan], 2),
                }),
              }}
            />
          </Elements>
        </RecurlyProvider>
      </SimplePageLayout>
    </>
  );
};

export const MembershipBillingInformation = (): JSX.Element => {
  const { t } = useTranslation();
  const deviceInfo = getDeviceInfo();
  const isMobilePlatform = Boolean(deviceInfo?.isMobilePlatform);

  return (
    <PageLayout
      data-cname="MembershipBillingInformationPage"
      allowAppBanner={false}
      headerBarProps={{
        action: isMobilePlatform ? 'noControls' : 'back',
        iconActions: getCameraActions(t),
      }}
    >
      <Content />
    </PageLayout>
  );
};
